<template>
  <!--
    No outer <teleport> here on purpose: @headlessui/vue's <Dialog> already
    renders its content through an internal <Portal> to <body>. Wrapping it in a
    second teleport double-portals the node, which makes Vue dereference a null
    `parentNode` on update/unmount ("Cannot read properties of null (reading
    'parentNode')"). Let Headless own the portaling.
  -->
  <TransitionRoot :show="modelValue" as="template" @after-leave="restoreFocus">
    <Dialog as="div" :class="['fixed inset-0', zClass]" @close="handleClose">
      <!-- Backdrop -->
      <TransitionChild
        as="template"
        enter="transition-opacity ease-out duration-150"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="transition-opacity ease-in duration-100"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-theme-bg/50 backdrop-blur-sm" aria-hidden="true" />
      </TransitionChild>

      <!-- Centering scroll container -->
      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4">
          <TransitionChild
            as="template"
            enter="transition ease-out duration-150"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="transition ease-in duration-100"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              :class="[
                'relative flex max-h-modal w-full flex-col overflow-hidden rounded-theme-lg border border-theme-border bg-theme-surface text-theme-text shadow-theme-dialog dark:shadow-theme-shadow/60',
                sizeClass,
                panelClass,
              ]"
            >
              <slot />
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
  /**
   * AppOverlay — the single overlay foundation for all modals/dialogs.
   *
   * Built on @headlessui/vue's Dialog so every modal gets focus-trap,
   * scroll-lock, return-focus, Escape, and aria-modal for free, plus one
   * themed panel container (bg/border/shadow/rounded) and z-index. Higher-level
   * components (AppModal, AppDialog) compose this; raw callers can use it
   * directly with a default-slot panel body.
   *
   * Centralising the panel chrome here means a styling fix (e.g. the
   * `bg-theme-surface` background) is made once for every modal in the app.
   */
  import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue';
  import { computed, nextTick, onUnmounted, watch } from 'vue';

  const props = defineProps({
    /** v-model — controls visibility. */
    modelValue: { type: Boolean, default: false },
    /** Optional stable launcher for flows that replace one dialog with another. */
    returnFocus: { type: Function, default: null },
    /** Panel max-width preset. */
    size: { type: String, default: 'md' },
    /**
     * When true (default), Escape / backdrop-click request a close and the
     * overlay hides itself. Set false to take full control via `@close`
     * (AppModal does this so it can guard `confirmLoading` and emit `cancel`).
     */
    dismissible: { type: Boolean, default: true },
    /** Extra classes merged onto the panel container. */
    panelClass: { type: [String, Array, Object], default: '' },
    /** Stacking context. Modals sit above drawers (z-drawer) by default. */
    zClass: { type: String, default: 'z-dialog' },
  });

  const emit = defineEmits(['update:modelValue', 'close']);
  let opener = null;
  let unmounted = false;
  // Headless can leave focus on the body; retain the actual opener before rendering.
  watch(
    () => props.modelValue,
    (open) => {
      if (open && !opener?.isConnected) opener = globalThis.document?.activeElement;
    },
    { immediate: true, flush: 'sync' }
  );

  async function restoreFocus() {
    await nextTick();
    if (!unmounted && props.modelValue) return;
    if (unmounted && globalThis.document?.activeElement?.closest('[role=dialog]')) return;
    const target = props.returnFocus?.() || opener;
    if (target?.isConnected) target.focus({ preventScroll: true });
    opener = null;
  }

  onUnmounted(() => {
    unmounted = true;
    restoreFocus();
  });

  const sizeClass = computed(() => {
    switch (props.size) {
      case 'sm':
        return 'max-w-md';
      case 'lg':
        return 'max-w-4xl';
      case 'xl':
        return 'max-w-6xl';
      case 'full':
        return 'max-w-[95vw]';
      default:
        return 'max-w-2xl';
    }
  });

  // Headless fires `@close` for both Escape and backdrop click (it cannot
  // distinguish the two). We surface it as `close`; when `dismissible` we also
  // hide ourselves so simple callers get auto-close for free.
  function handleClose() {
    emit('close');
    if (props.dismissible) {
      emit('update:modelValue', false);
    }
  }
</script>
